import { GoogleTagEvent, logWithGoogleTagAndTopicOrganization } from '../helpers/api/api';
import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators';
import { delay, isDefined } from '../helpers/utils';

import { GetPointResponse } from '../types';
import { StateController } from '../helpers/state/state-management/state-controller';
import accountInfo from '../helpers/state/account-info';
import challengeData from '../helpers/state/challenge-data';
import { getMainPage } from '../helpers/page-name';
import { getPoint } from '../helpers/api/points-api';
import { getQueryStringParameters } from '../helpers/urls';
import { ifDefined } from 'lit/directives/if-defined';
import { loadGoogleMapsApi } from '../helpers/api/google-api';
import pointCache from '../helpers/state/point-cache';
import topicData from '../helpers/state/topic-data';
import { redirectToNewUiPathWithTransfer } from '../helpers/auth-cookie';

@customElement('point-page')
export class PointPage extends LitElement {
    amplitude_callsite: string = 'Point Page';

    @property({ type: String })
    point_id?: string;

    @property({ type: Object })
    point: GetPointResponse | undefined;

    @property({ type: Boolean })
    canUpgradePoint?: boolean;

    @state()
    hideMapLink: boolean = false;


    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindAccountState = new StateController(this, accountInfo);

    async firstUpdated() {
        await this.startup();
    }

    private async startup() {
        const queryString = getQueryStringParameters();
        const point_id = queryString.get('point_id');
        const point_key = queryString.get('point_key') ?? undefined;

        // `?new_experience=true` opt-in for legacy edit links to forward
        // to the new UI's `/p/<id>/edit` page. We do this before any data
        // loading or Google Maps boot so the recipient lands on the new
        // page immediately. Authenticated users get a one-time transfer
        // token in the hash so their session crosses domains.
        if (queryString.get('new_experience') === 'true' && point_id) {
            const targetPath = point_key
                ? `/p/${point_id}/edit?point_key=${encodeURIComponent(point_key)}`
                : `/p/${point_id}/edit`;
            await redirectToNewUiPathWithTransfer(targetPath);
            return;
        }

        await loadGoogleMapsApi();
        this.hideMapLink = queryString.get('hide_map_link') === 'true';

        await topicData.instantiateTopicData(
            this.amplitude_callsite,
            accountInfo.isAuthenticated,
            false,
            true
        );
        if (isDefined(topicData.topic)) {
            // Pass the URL's point_id/point_key as the per-point edit-link fallback so the points
            // load works on a now-private topic where topic_key is empty.
            await pointCache.instantiatePointCache(
                topicData.topic,
                topicData.topicKey,
                false,
                true,
                challengeData.allCheckins,
                point_id ?? undefined,
                point_key
            );
        }

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const canUpgradePoint = false; // update when we have the right permissions

        // Get the topic and block.
        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            if (point_id === undefined) {
                return;
            }

            this.point_id = point_id;
            // Prefer the URL's point_key (per-point unique edit link); fall back to topic_key for
            // legacy callers where topicKey grants point read access via the topic key.
            this.point = await getPoint(this.amplitude_callsite, this.point_id, point_key ?? topicData.topicKey);
            logWithGoogleTagAndTopicOrganization(this.amplitude_callsite, GoogleTagEvent.EDIT_POINT_PAGE_OPENED, {
                point_id: this.point_id,
            });
        }, 'An error has occurred.  Context: could not get the topic or point from the server.');
        window.dispatchEvent(new CustomEvent('page-content-ready'));
    }

    private async onDeletePoint() {
        getMainPage().errorToastElement.showToast('Deleting point...');
        await delay(1500);
        // redirect to command center
        window.location.replace(window.location.href.replace('point', 'command-center'));
    }

    private async openThisMap() {
        // get topic key
        const queryString = window.location.href.split('?')[1];
        const params = new URLSearchParams(queryString);
        const topicKey = params.get('topic_key');

        // open URL
        const openURL = `${window.location.origin}/#command-center?topic_id=${topicData.topicId}&topic_key=${topicKey ?? ''}&point_id=${this.point_id ?? ''}`;
        window.open(openURL, '_blank');
    }

    render() {
        return topicData.isReady
            ? html`
                <div class="points__page_wrapper">
                    <div class="points__content_wrapper">
                        <div class="points__header">
                            ${this.hideMapLink ? '' : html`
                                <map-header>
                                    <form-button size="sm" class="points__view_map" variant="custom" style="--btn-border-color:${topicData.topic?.header_text_color};--btn-label-color:${topicData.topic?.header_text_color};--btn-background-color:transparent;" @click="${async () => await this.openThisMap()}">
                                    <mwc-icon>open_in_new</mwc-icon>
                                    Open Map
                                    </form-button>
                                </map-header>
                            `}
                        </div>
                        <div class="points__middle_content">
                        ${this.canUpgradePoint === true ? html`
                        <div class="points__upgrade_section">
                            <h2 class="hide_on_mobile">Upgrade Your Point</h2>
                            <p class="hide_on_mobile">Stand out from the crowd and make your point glow on the map.<p>
                            <div class="points__upgrade_section_items hide_on_mobile">
                                <div class="points__upgrade_section_item map_views_section">
                                    <h4>This Map Earns</h4>
                                    <div class="inline_words">
                                    <h2>${topicData.topic?.statistics.approximate_topic_thirty_day_views}</h2> <p>Views Per Month</p>
                                    </div>
                                </div>
                                <div class="points__upgrade_section_item info_section">
                                    <img
                                        src="/images/sparkle.png"
                                        class="sparkles"
                                        alt="Sparkles"
                                    />
                                    <p>A glowing ${topicData.topic?.point_name ?? 'point'} increases the chances of a click by 70%</p>
                                </div>
                                <div class="points__upgrade_section_item upgrade_section">
                                    <div class="inline_words">
                                    <h2>$1</h2> <p>per Day</p>
                                    </div>
                                    <p>Choose start and end dates</p>

                                    <vaadin-date-picker
                                        id="startDate"
                                        class="input"
                                        placeholder="Start Date"
                                        ></vaadin-date-picker>
                                    <vaadin-date-picker
                                        id="endDate"
                                        class="input"
                                        placeholder="End Date"
                                        ></vaadin-date-picker>

                                    <form-button size="md" class="points__upgrade_button">
                                    Upgrade Your Point Now
                                    </form-button>
                                </div>
                            </div>
                            <div class="points__upgrade_section_item info_section hide_on_desktop">
                                <div class="inline_words">
                                <img
                                    src="/images/sparkle.png"
                                    class="sparkles"
                                    alt="Sparkles"
                                />
                                <h2>Upgrade your ${topicData.topic?.point_name ?? 'point'}</h2>
                                <img
                                src="/images/sparkle.png"
                                class="sparkles"
                                alt="Sparkles"
                            />
                                </div>
                                <p> A glowing point increases the chances of a click by 70%</p>
                                <form-button size="md" class="points__upgrade_button">
                                Learn More
                                </form-button>

                            </div>
                        </div>` :''}
                        <div class="points__add_point_section">
                        <div class="points__add_point_header">
                        <h2>${this.point ? 'Edit Your' : 'Add a'} ${topicData.topic?.point_name ?? 'Point'}</h2>
                        ${this.point ? html`
                            <p>Make changes to your ${topicData.topic?.point_name ?? 'point'} on the map</p>
                        ` : ''}
                        </div>

                        <add-edit-point-component
                            class="points__form"
                            @on-delete-point="${this.onDeletePoint}"
                            point_id="${ifDefined(this.point_id)}"
                            .point="${this.point}"
                            isPointPage
                            .editMode=${!!this.point}
                            hideTitle
                            @close-point-modal="${async () => {
                                const refetchKey = (getQueryStringParameters().get('point_key') ?? undefined) ?? topicData.topicKey;
                                this.point = await getPoint(this.amplitude_callsite, this.point_id ?? '', refetchKey);
                            }}"
                        >
                        </add-edit-point-component>
                        </div>
                        </div>
                    </div>
                </div>
                <error-toast id="errorToast"></error-toast> `
            : '';
    }

    static get styles() {
        return css`
            .points__page_wrapper {
                display: flex;
                flex-direction: column;
                align-items: center;
                padding: 50px 15%;
            }

            .points__content_wrapper {
                display: flex;
                flex-direction: column;
                width: 100%;
                max-width: 1200px;
                justify-content: center;
                background-color: white;
                border: 1px solid black;
            }

            .points__view_map {
                display: flex;
                flex-direction: column;
                align-items: flex-end;
                --btn-label-color: white;
                border-radius: 10px 10px 0 0;
            }

            .points__middle_content {
                display: flex;
                flex-direction: row;
                justify-content: center;
                align-items: top;
                padding: 0 25px;
                gap: 25px;
                min-width: 0;
                box-sizing: border-box;
            }

            .points__upgrade_section {
                flex: 1 1 30%;
                border-right: 1px solid #e5e5e5;
                padding-right: 25px;
                justify-text: left;
            }

            .points__add_point_section {
                flex: 1 1 65%;
                padding-left: 10px;
                min-width: 0;
                max-width: 100%;
                box-sizing: border-box;
            }

            .points__form {
                height: calc(100vh - 220px);
                min-height: 400px;
            }

            .points__add_point_header {
                text-align: center;
            }

            p {
                margin-top: 5px;
                margin-bottom: 0;
            }

            h2 {
                margin-bottom: 0px;
            }

            h4 {
                margin-top: 5px;
                margin-bottom: 5px;
            }

            .inline_words {
                display: inline-flex;
                flex-direction: row;
                align-items: flex-end;
                justify-content: flex-start;
                gap: 5px;
            }

            .inline_words p {
                margin-bottom: 5px;
            }

            .inline_words h2 {
                margin-top: 0;
            }

            .points__upgrade_section_items {
                display: flex;
                flex-direction: column;
                gap: 10px;
                width: 100%;
                margin-top: 10px;
            }

            .points__upgrade_button {
                --btn-background-color: var(--main-secondary-color);
                margin-top: 10px;
            }

            .points__upgrade_section_item {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                justify-content: flex-start;
                padding: 10px;
                border-radius: 10px;
            }

            .info_section {
                border: .5px solid var(--main-primary-color);
                display: flex;
                flex-direction: row;
                justify-content: flex-start;
            }

            .map_views_section {
                background-color: var(--main-primary-color);
                color: white;
                text-color: white;
            }

            .upgrade_section {
                border: .5px solid var(--main-primary-color);
            }

            .sparkles {
                height: 20px;
            }

            .hide_on_desktop {
                display: none;
            }

            @media only screen and (max-width: 550px) {
                .points__page_wrapper {
                    padding: 0;
                }

                .points__middle_content {
                    flex-direction: column;
                    align-items: center;
                    padding: 10px;
                }

                .points__upgrade_section {
                    padding-right: 0;
                    border-right: none;
                }

                .points__add_point_section {
                    flex: 0 0 auto;
                }

                .hide_on_mobile {
                    display: none;
                }

                .info_section {
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    border: .5px solid var(--main-primary-color);
                    gap: 5px;
                    text-align: center;
                }

                .hide_on_desktop {
                    display: flex;
                }

                .points__form {
                    height: auto;
                    min-height: 0;
                }

            }



        `;
    }
}

